import { ChangeDetectionStrategy, Component, inject, OnDestroy } from "@angular/core";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { ListService } from "common/list/list.service";
import { MultiLevelSelectionService } from "common/multi-level-selection.service";
import { WorkspaceSelected, WorkspaceSelectionComponent } from "common/workspace/workspace-selection.component";
import { MainService } from "core/main.service";
import { ActionStateWrapper, BaseNavigationComponent } from "navigation/list/base/base-navigation.component";
import { NavigationConfiguration } from "navigation/list/navigation-configuration.interface";
import { ListNavigationEntry } from "navigation/list/navigation-entry.interface";
import { NavigationService } from "navigation/list/navigation.service";
import { Subject } from "rxjs";
import { AsyncPipe, NgClass } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { SingleLineNavigationEntryComponent } from "navigation/list/entry/single-line-navigation-entry.component";
import { TwoLineNavigationEntryComponent } from "navigation/list/entry/two-line-navigation-entry.component";
import {
	TwoLineSplitRatingNavigationEntryComponent
} from "navigation/list/entry/two-line-split-rating-navigation-entry.component";
import { State } from "core/volatile-application-state.model";
import { Breakpoint, LayoutService } from "core/layout.service";
import { takeUntil } from "rxjs/operators";

@Component({
	selector: "stages-clipboard",
	templateUrl: "./clipboard.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		NgClass,
		TranslateModule,
		RouterLink,
		WorkspaceSelectionComponent,
		SingleLineNavigationEntryComponent,
		TwoLineNavigationEntryComponent,
		TwoLineSplitRatingNavigationEntryComponent,
		AsyncPipe,
	],
})
export class ClipboardComponent extends BaseNavigationComponent implements OnDestroy {
	private navigationService = inject(NavigationService);
	private mainService = inject(MainService);
	private listService = inject(ListService);
	protected layoutService = inject(LayoutService);

	workspaceNavigationOpen = false;

	navigationConfiguration?: NavigationConfiguration;
	private destroy$ = new Subject<boolean>();

	constructor() {
		const route = inject(ActivatedRoute);
		const router = inject(Router);
		const selectionService = inject<MultiLevelSelectionService<ListNavigationEntry>>(MultiLevelSelectionService);

		super(route, router, selectionService);
		this.navigationService.navigationConfiguration$
			.pipe(takeUntil(this.destroy$))
			.subscribe((navigationConfiguration) => {
				this.navigationConfiguration = navigationConfiguration;
				if (!navigationConfiguration.clipboardConfiguration.workspaceChangeSupported) {
					this.workspaceNavigationOpen = false;
				}
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next(true);
		this.destroy$.complete();
	}

	isClipboardOpen(navigationConfiguration: NavigationConfiguration): boolean {
		if (navigationConfiguration.activatedRoute) {
			const params = navigationConfiguration.activatedRoute.snapshot.paramMap;
			const action = params.get("action");
			return action !== null && action.indexOf("paste") !== -1;
		}
		return false;
	}

	isUpEnabled(navigationConfiguration: NavigationConfiguration): boolean {
		return navigationConfiguration.parentRoute !== undefined && !this.workspaceNavigationOpen;
	}

	isWorkspaceNavEnabled(navigationConfiguration: NavigationConfiguration): boolean {
		return navigationConfiguration.clipboardConfiguration.workspaceChangeSupported;
	}

	toggleWorkspaceNavigation(): void {
		this.workspaceNavigationOpen = !this.workspaceNavigationOpen;
	}

	getElementCount(navigationConfiguration: NavigationConfiguration): number {
		const selection = this.getSelection(navigationConfiguration, true);
		if (!selection) {
			return 0;
		}
		return selection.getSelectedCount();
	}

	async onSelectWorkspace(
		workspace: WorkspaceSelected,
		navigationConfiguration: NavigationConfiguration,
	): Promise<void> {
		this.workspaceNavigationOpen = false;

		const paramMap = navigationConfiguration.activatedRoute!.snapshot.paramMap;

		this.inProgress = true;
		try {
			await this.router.navigate([
				"workspace",
				workspace.id,
				paramMap.get("processVersion"),
				...navigationConfiguration.clipboardConfiguration.routeAfterWorkspaceChange,
				this.reuseNavigationParams(paramMap),
			]);
		} finally {
			this.inProgress = false;
		}
	}

	isNavigationDrawerOpen(breakpoint: Breakpoint): boolean {
		return (
			this.mainService.applicationState.navigationDrawerOpen === State.FORCE_OPENED ||
			(!breakpoint.isMobile && this.mainService.applicationState.navigationDrawerOpen !== State.FORCE_CLOSED)
		);
	}

	getCurrentActions(navigationConfiguration: NavigationConfiguration): ActionStateWrapper[] {
		const actionName = this.getActionParam(navigationConfiguration);
		const result: Array<ActionStateWrapper> = [];
		if (navigationConfiguration.clipboardConfiguration) {
			const currentMode = this.getCurrentMode(
				navigationConfiguration.clipboardConfiguration.supportedModes,
				actionName,
			);
			if (currentMode.uiActions.length > 0) {
				const selection = this.getSelection(navigationConfiguration, true);
				for (const a of currentMode.uiActions) {
					result.push(
						new ActionStateWrapper(
							a,
							this,
							selection,
							navigationConfiguration.activatedRoute!,
							navigationConfiguration.baseRoute!,
							() => navigationConfiguration.invalidate(),
						),
					);
				}
			}
		}
		return result;
	}

	getVisibleNavigationEntries(completeList: ListNavigationEntry[]): ListNavigationEntry[] {
		return this.listService.getVisibleListItems(completeList) as ListNavigationEntry[];
	}

	hasOmmittedEntries(completeList: ListNavigationEntry[]): boolean {
		return this.listService.hasOmmittedItems(completeList);
	}

	protected readonly State = State;
}
